import { getAppConfig } from './configService';

// Resolved once config.json loads (or immediately from process.env in local dev).
// Every API method awaits this before reading BASE_API / FUNCTION_KEY so the
// values are guaranteed to be set before the first request leaves the browser.
let BASE_API = process.env.REACT_APP_API_BASE_URL || '';
let FUNCTION_KEY = process.env.REACT_APP_FUNCTION_KEY || '';

const configReady: Promise<void> = getAppConfig().then(cfg => {
	BASE_API = cfg.apiBaseUrl;
	FUNCTION_KEY = cfg.functionKey;
}).catch(() => {
	// Keep process.env fallbacks already assigned above
});

async function apiUrl(): Promise<string> {
	await configReady;
	return BASE_API;
}

async function getDefaultHeaders(): Promise<HeadersInit> {
	await configReady;
	const headers: HeadersInit = {
		'Content-Type': 'application/json',
	};

	if (FUNCTION_KEY) {
		headers['x-functions-key'] = FUNCTION_KEY;
	}

	return headers;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success: boolean;
}

export interface AuthUser {
	user_id: string;
	email: string;
	display_name: string | null;
	first_name: string | null;
	last_name: string | null;
	status: 'active' | 'suspended';
}

export interface OrgMetadataSummary {
  orgId: string;
	workspaceId?: string;
	snapshotId?: string;
  timestamp: string;
  status: string;
  fileCount: number;
  zipBlobUri?: string;
}

export interface CreateSnapshotResponse {
	instanceId: string;
	statusQueryUrl: string;
}

export interface OrchestrationStatus {
  instanceId: string;
  name: string;
  runtimeStatus: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Canceled' | 'Terminated';
  createdTime: string;
  lastUpdatedTime: string;
  input?: any;
  output?: any;
  history?: any;
  customStatus?: {
    step?: number;
    stepName?: string;
    progress?: number;
    message?: string;
  };
}

// Analysis interfaces
export interface AnalysisRun {
	run_id: string;
	org_id: string;
	snapshot_id: string;
	workspace_id: string | null;
	triggered_by: string;
	status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed';
	started_at: string;
	completed_at: string | null;
	error_info: string | null;
	job_execution_name: string | null;
}

export interface AnalysisRunsResponse {
	runs: AnalysisRun[];
}

export interface CreateAnalysisRunResponse {
	runId: string;
	status: string;
	startedAt: string;
	jobExecutionName: string | null;
}

export interface AnalysisStep {
	step_id: string;
	step_name: string;
	step_order: number;
	depends_on: string[];
	status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
	started_at: string | null;
	completed_at: string | null;
	items_processed: number | null;
	items_total: number | null;
	error_info: string | null;
}

export interface AnalysisRunDetailResponse extends AnalysisRun {
	steps: AnalysisStep[];
}

export interface AnalysisResultsResponse {
	run_id: string;
	result_type: string;
	total: number;
	results: Record<string, any>[];
}

// New API interfaces for conversations and responses
export interface Conversation {
  id: string;
  object: 'conversation';
  org_id: string;
	workspace_id?: string | null;
	snapshot_id?: string | null;
  created_at: number; // Unix timestamp in seconds
  updated_at: number; // Unix timestamp in seconds
  topic?: string | null;
}

export interface ConversationListResponse {
  object: 'list';
  data: Conversation[];
  next_cursor: { timestamp: number; conversation_id: string } | null;
  has_more: boolean;
}

export interface ConversationDetailResponse {
  id: string;
  object: 'conversation';
  org_id: string;
	workspace_id?: string | null;
	snapshot_id?: string | null;
  created_at: number; // Unix timestamp in seconds
  updated_at: number; // Unix timestamp in seconds
  topic?: string | null;
  responses: Response[];
}

export interface Response {
  id: string;
  object: 'response';
  org_id: string;
  conversation_id: string;
	snapshot_id?: string | null;
  previous_response_id: string | null;
  status: 'queued' | 'in_progress' | 'completed' | 'failed';
  created_at: number; // Unix timestamp in seconds
  completed_at: number | null; // Unix timestamp in seconds
  items: ApiResponseItem[] | null;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  } | null;
  error: {
    code: string;
    message: string;
    details: any;
  } | null;
}

// Discriminated union for response items
export type ApiResponseItem =
  | ApiMessageItem
  | ApiToolCallItem
  | ApiToolOutputItem
  | ApiReasoningItem
  | ApiErrorItem;

export interface ApiMessageItem {
  type: 'message';
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string | ApiContentPart[];
  created_at: number; // Unix timestamp in seconds
}

export interface ApiContentPart {
  type: 'text';
  text: string;
}

export interface ApiToolCallItem {
  type: 'tool_call';
  id: string;
  agent_action_id: string | null;
  tool_name: string;
  tool_type: 'function' | 'agent';
  codex_tool_type: 'command_execution' | 'mcp_tool_call' | 'file_change' | 'web_search' | 'todo_list' | null;
  arguments: object | string;
  status: 'in_progress' | 'completed' | 'failed';
  created_at: number; // Unix timestamp in seconds
}

export interface ApiToolOutputItem {
  type: 'tool_output';
  id: string;
  agent_action_id: string | null;
  tool_call_id: string;
  status: 'in_progress' | 'completed' | 'failed';
  output: string | object | null;
  error: {
    message: string;
    details: object | null;
  } | null;
  created_at: number; // Unix timestamp in seconds
}

export interface ApiReasoningItem {
  type: 'reasoning';
  id: string;
  agent_id: string | null;
  content: string;
  summary: string | null;
  created_at: number; // Unix timestamp in seconds
}

export interface ApiErrorItem {
  type: 'error';
  id: string;
  error_code: string;
  message: string;
  recoverable: boolean;
  details: object | null;
  created_at: number; // Unix timestamp in seconds
}

// SSE Stream Event types
export interface ResponseStreamEvent {
  type: string;
  sequence_number: number;
  response_id: string;
  conversation_id: string;
  [key: string]: any; // Event-specific fields vary by type
}

export interface ResponseCreatedEvent extends ResponseStreamEvent {
  type: 'response.created';
}

export interface ResponseInProgressEvent extends ResponseStreamEvent {
  type: 'response.in_progress';
}

export interface ResponseCompletedEvent extends ResponseStreamEvent {
  type: 'response.completed';
}

export interface ResponseFailedEvent extends ResponseStreamEvent {
  type: 'response.failed';
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface MessageDoneEvent extends ResponseStreamEvent {
  type: 'response.message.done';
  content: string;
}

export interface ToolCallCreatedEvent extends ResponseStreamEvent {
  type: 'response.tool_call.created';
  tool_name: string;
  tool_type: 'function' | 'agent';
}

export interface ToolCallInProgressEvent extends ResponseStreamEvent {
  type: 'response.tool_call.in_progress';
  tool_name: string;
}

export interface ToolOutputDeltaEvent extends ResponseStreamEvent {
  type: 'response.tool_output.delta';
  delta: string;
}

export interface ToolOutputDoneEvent extends ResponseStreamEvent {
  type: 'response.tool_output.done';
  output: string | object;
}

export interface ReasoningDoneEvent extends ResponseStreamEvent {
  type: 'response.reasoning.done';
  content: string;
}

export interface ResponseErrorEvent extends ResponseStreamEvent {
  type: 'error';
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface ConversationTopicUpdatedEvent extends ResponseStreamEvent {
  type: 'conversation.topic.updated';
  topic: string;
}

class ApiService {
  private sessionTokenCache: string | null = null;
  private sessionTokenCacheExpiry: number = 0;

  /**
   * Get session token for cross-origin requests (e.g., workspace-runtime)
   * Caches the token to avoid repeated API calls
   */
  private async getSessionToken(): Promise<string | null> {
    // Return cached token if still valid (cache for 5 minutes)
    if (this.sessionTokenCache && Date.now() < this.sessionTokenCacheExpiry) {
      return this.sessionTokenCache;
    }

    try {
      const response = await fetch(`${await apiUrl()}/api/auth/me?include_token=true`, {
        method: 'GET',
        headers: await getDefaultHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (data.sessionToken) {
        this.sessionTokenCache = data.sessionToken;
        this.sessionTokenCacheExpiry = Date.now() + 5 * 60 * 1000; // 5 minutes
        return data.sessionToken;
      }
      return null;
    } catch (error) {
      console.error('Failed to get session token:', error);
      return null;
    }
  }

  /**
   * Clear session token cache (e.g., on logout)
   */
  clearSessionTokenCache(): void {
    this.sessionTokenCache = null;
    this.sessionTokenCacheExpiry = 0;
  }

  /**
   * Request a magic link for login
   */
  async requestMagicLink(email: string): Promise<ApiResponse<{ success: boolean; senderAddress?: string }>> {
    try {
      const response = await fetch(`${await apiUrl()}/api/auth/magic/request`, {
        method: 'POST',
        headers: await getDefaultHeaders(),
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json().catch(() => ({ success: true }));
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to request magic link' };
    }
  }

  /**
   * Consume magic link and create session
   */
  async consumeMagicLink(token: string): Promise<ApiResponse<{ user: AuthUser }>> {
    try {
      const response = await fetch(`${await apiUrl()}/api/auth/magic/consume`, {
        method: 'POST',
        headers: await getDefaultHeaders(),
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to consume magic link' };
    }
  }

	/**
	 * Update authenticated user's profile (name)
	 */
	async updateProfile(firstName: string, lastName: string): Promise<ApiResponse<{ user: AuthUser }>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/auth/profile`, {
				method: 'POST',
				headers: await getDefaultHeaders(),
				credentials: 'include',
				body: JSON.stringify({
					first_name: firstName,
					last_name: lastName,
				}),
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to update profile' };
		}
	}

  /**
   * Logout current session
   */
  async logout(): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${await apiUrl()}/api/auth/logout`, {
        method: 'POST',
        headers: await getDefaultHeaders(),
        credentials: 'include',
      });

      if (!response.ok && response.status !== 204) {
        const errorText = await response.text();
        return { success: false, error: errorText };
      }

      // Clear session token cache on logout
      this.clearSessionTokenCache();

      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to logout' };
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<ApiResponse<{ user: AuthUser }>> {
    try {
      const response = await fetch(`${await apiUrl()}/api/auth/me`, {
        method: 'GET',
        headers: await getDefaultHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get current user' };
    }
  }

  /**
   * Start OAuth flow - get authorization URL
   */
  async oauthStart(loginHost?: string): Promise<ApiResponse<{ authorizeUrl: string; state: string }>> {
    try {
      const params = new URLSearchParams();
      if (loginHost) params.append('login_host', loginHost);

      const url = `${await apiUrl()}/api/oauth/start${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: await getDefaultHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.text();
        return { success: false, error };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to start OAuth flow' };
    }
  }

  /**
   * Handle OAuth callback - exchange code for workspace
   */
  async oauthCallback(code: string, state: string): Promise<ApiResponse<{
		workspace: {
			workspace_id: string;
			org_id: string;
			connection_id: string;
			owner_id: string | null;
			snapshot_id: string | null;
			container_app_id: string | null;
			container_app_url: string | null;
			file_share_name: string | null;
			infra_status: 'pending' | 'running' | 'completed' | 'failed';
			infra_started_at: string | null;
			infra_completed_at: string | null;
			infra_error_info: string | null;
			name: string;
			type: string;
			created_at: string;
			last_selected_at: string | null;
		};
		connection: {
			connection_id: string;
			label: string;
			sf_org_id: string;
			sf_username: string;
			sf_instance_url: string;
			is_sandbox: boolean;
		};
		isNewWorkspace: boolean;
	}>> {
    try {
      const params = new URLSearchParams({ code, state });
      const response = await fetch(`${await apiUrl()}/api/oauth/callback?${params.toString()}`, {
        method: 'GET',
        headers: await getDefaultHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.text();
        return { success: false, error };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to complete OAuth flow' };
    }
  }

	/**
	 * List workspaces for the current user
	 */
	async getWorkspaces(): Promise<ApiResponse<{ workspaces: Array<{
		workspace_id: string;
		org_id: string;
		connection_id: string;
		owner_id: string | null;
		snapshot_id: string | null;
		container_app_id: string | null;
		container_app_url: string | null;
		file_share_name: string | null;
		infra_status: 'pending' | 'running' | 'completed' | 'failed';
		infra_started_at: string | null;
		infra_completed_at: string | null;
		infra_error_info: string | null;
		snapshot_apply_warnings: string | null; // JSON: { skipped_count: number; skipped_files: string[] }
		name: string;
		type: string;
		created_at: string;
		last_selected_at: string | null;
		connection: {
			connection_id: string;
			label: string;
			sf_org_id: string;
			sf_username: string;
			sf_instance_url: string;
			is_sandbox: boolean;
			created_at: string;
			last_used_at: string | null;
			last_selected_at: string | null;
		};
	}> }>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/workspaces`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

			if (!response.ok) {
				const error = await response.text();
				return { success: false, error };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get workspaces' };
		}
	}

	/**
	 * Get a single workspace by ID
	 */
	async getWorkspace(workspaceId: string): Promise<ApiResponse<{
		workspace_id: string;
		org_id: string;
		connection_id: string;
		owner_id: string | null;
		snapshot_id: string | null;
		container_app_id: string | null;
		container_app_url: string | null;
		file_share_name: string | null;
		infra_status: 'pending' | 'running' | 'completed' | 'failed';
		infra_started_at: string | null;
		infra_completed_at: string | null;
		infra_error_info: string | null;
		snapshot_status: 'pending' | 'running' | 'completed' | 'failed' | null;
		snapshot_progress: number | null;
		snapshot_message: string | null;
		snapshot_step_name: string | null;
		snapshot_last_updated_at: string | null;
		snapshot_error_info: string | null;
		snapshot_apply_status: 'pending' | 'running' | 'completed' | 'failed' | null;
		snapshot_apply_progress: number | null;
		snapshot_apply_message: string | null;
		snapshot_apply_started_at: string | null;
		snapshot_apply_completed_at: string | null;
		snapshot_apply_error_info: string | null;
		snapshot_apply_warnings: string | null; // JSON: { skipped_count: number; skipped_files: string[] }
		name: string;
		type: string;
		created_at: string;
		last_selected_at: string | null;
		connection: {
			connection_id: string;
			label: string;
			sf_org_id: string;
			sf_username: string;
			sf_instance_url: string;
			is_sandbox: boolean;
			created_at: string;
			last_used_at: string | null;
			last_selected_at: string | null;
		};
	}>> {
		try {
			const params = new URLSearchParams();
			params.append('workspaceId', workspaceId);

			const response = await fetch(`${await apiUrl()}/api/workspace?${params.toString()}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

			if (!response.ok) {
				if (response.status === 404) {
					return { success: false, error: 'Workspace not found' };
				}
				const error = await response.text();
				return { success: false, error };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get workspace' };
		}
	}

	/**
	 * Update the last selected timestamp for a workspace
	 */
	async updateWorkspaceLastSelected(workspaceId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/workspaces/${encodeURIComponent(workspaceId)}/last-selected`, {
				method: 'POST',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});
			
			if (!response.ok) {
				const error = await response.text();
				return { success: false, error };
			}
		
			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to update last selected timestamp' };
		}
	}

  /**
	 * Get the current metadata summary for an organization.
	 */
	async getOrgMetadata(workspaceId: string): Promise<ApiResponse<OrgMetadataSummary>> {
		try {
			if (!workspaceId) {
				return { success: false, error: 'workspaceId is required' };
			}

			const params = new URLSearchParams();
			params.append('workspaceId', workspaceId);

			const response = await fetch(`${await apiUrl()}/api/org-metadata?${params.toString()}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

			if (!response.ok) {
				if (response.status === 404) {
					return { success: false, error: 'Metadata not found' };
				}
				const error = await response.text();
				return { success: false, error };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get org metadata' };
		}
	}

	/**
	 * Start a metadata snapshot for an organization.
	 */
	async startMetadataSnapshot(workspaceId?: string): Promise<ApiResponse<CreateSnapshotResponse>> {
		try {
			const body: any = {};
			if (workspaceId) body.workspaceId = workspaceId;

			const response = await fetch(`${await apiUrl()}/api/metadata-snapshots`, {
				method: 'POST',
				headers: await getDefaultHeaders(),
				credentials: 'include',
				body: JSON.stringify(body)
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage: string;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.message || errorJson.error || errorText;
				} catch {
					errorMessage = errorText;
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to start metadata snapshot' };
		}
	}

	/**
	 * Get the status of a metadata snapshot orchestration.
	 */
	async getRetrievalStatus(instanceId: string): Promise<ApiResponse<OrchestrationStatus>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/status/${encodeURIComponent(instanceId)}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

			if (!response.ok) {
				if (response.status === 404) {
					return { success: false, error: 'Orchestration instance not found' };
				}
				const error = await response.text();
				return { success: false, error };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get snapshot status' };
		}
	}

  /**
   * Get paginated list of conversations for an organization.
   */
	async getConversations(
		workspaceId: string,
		options?: {
			limit?: number;
			order?: 'asc' | 'desc';
			cursorTimestamp?: number;
			cursorConversationId?: string;
		}
	): Promise<ApiResponse<ConversationListResponse>> {
		try {
			const params = new URLSearchParams();
			params.append('workspace_id', workspaceId);

      if (options?.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options?.order) {
        params.append('order', options.order);
      }
      if (options?.cursorTimestamp && options?.cursorConversationId) {
        params.append('cursor_timestamp', options.cursorTimestamp.toString());
        params.append('cursor_conversation_id', options.cursorConversationId);
      }

			const response = await fetch(`${await apiUrl()}/api/conversations?${params.toString()}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get conversations' };
		}
	}

  /**
   * Get a conversation with all responses and items.
   */
	async getConversation(conversationId: string): Promise<ApiResponse<ConversationDetailResponse>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/conversations/${encodeURIComponent(conversationId)}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: 'Conversation not found' };
        }
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get conversation' };
		}
	}

	async deleteConversation(conversationId: string): Promise<ApiResponse<void>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/conversations/${encodeURIComponent(conversationId)}`, {
				method: 'DELETE',
				headers: await getDefaultHeaders(),
				credentials: 'include',
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			return { success: true };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to delete conversation' };
		}
	}

	async archiveConversation(conversationId: string): Promise<ApiResponse<Conversation>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/conversations/${encodeURIComponent(conversationId)}`, {
				method: 'PATCH',
				headers: await getDefaultHeaders(),
				credentials: 'include',
				body: JSON.stringify({ archived: true }),
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to archive conversation' };
		}
	}

	async renameConversation(conversationId: string, topic: string): Promise<ApiResponse<Conversation>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/conversations/${encodeURIComponent(conversationId)}`, {
				method: 'PATCH',
				headers: await getDefaultHeaders(),
				credentials: 'include',
				body: JSON.stringify({ topic }),
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to rename conversation' };
		}
	}

  /**
   * Create a response with optional streaming.
   * If stream is true, returns a cleanup function for SSE streaming.
   * If stream is false, returns the Response object.
   * @param containerAppUrl - The URL of the workspace-runtime container app (from auth response)
   */
  async createResponse(
    input: string,
    previousResponseId?: string | null,
    stream: boolean = true,
    containerAppUrl?: string | null,
    onEvent?: (event: ResponseStreamEvent) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): Promise<(() => void) | ApiResponse<Response>> {
    if (!containerAppUrl) {
      return { success: false, error: 'Container app URL is required. Please authenticate first.' };
    }

    const requestBody = {
      input,
      previous_response_id: previousResponseId || null,
      stream,
    };

    if (stream) {
      // Streaming: use fetch with ReadableStream to parse SSE
			return this.createResponseStream(
				input,
				previousResponseId || null,
				containerAppUrl,
				onEvent || (() => {}),
				onError,
				onComplete
			);
    } else {
      // Non-streaming: expect 202 Accepted response with response metadata
      // Call workspace-runtime container directly
    try {
      // Get session token for cross-origin request
      const sessionToken = await this.getSessionToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${containerAppUrl}/responses`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

        if (response.status === 202) {
          // 202 Accepted - response is queued, return initial response data
          const data = await response.json();
          // Convert to Response format expected by the frontend
          const responseData: Response = {
            id: data.response_id,
            object: 'response',
            org_id: data.org_id,
            conversation_id: data.conversation_id,
				    snapshot_id: data.snapshot_id || null,
            previous_response_id: data.previous_response_id,
            status: data.status || 'queued',
            created_at: data.created_at,
            completed_at: null,
            items: null,
            usage: null,
            error: null,
          };
          return { success: true, data: responseData };
        }

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = errorText;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorJson.message || errorText;
          } catch {
            // Use errorText as-is if not JSON
          }
          return { success: false, error: errorMessage };
        }

        // Fallback for other success status codes (shouldn't happen with new implementation)
        const data = await response.json();
        return { success: true, data };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Failed to create response' };
      }
    }
  }

  /**
   * Poll for response status updates with exponential backoff.
   * Stops when status is 'completed' or 'failed'.
   * Resets polling timer when new response items are detected (indicating progress).
   * Supports abort signal for cancellation.
   */
  async pollResponseStatus(
    responseId: string,
    onUpdate?: (response: Response) => void,
    maxAttempts: number = 16, // ~1 minute with exponential backoff
    abortSignal?: AbortSignal
  ): Promise<ApiResponse<Response>> {
    let attempt = 0;
    let delay = 500; // Start with 500ms delay
    let previousItemCount = 0;

    while (attempt < maxAttempts) {
      // Check if polling was cancelled at the start of each iteration
      if (abortSignal?.aborted) {
        throw new Error('Polling cancelled');
      }

      attempt++;
      
			const result = await this.getResponse(responseId);
      
      if (!result.success || !result.data) {
        return result;
      }

      const response = result.data;
      
      // Count current response items
      const currentItemCount = response.items?.length || 0;
      
      // If new items were added, reset polling timer (progress detected)
      if (currentItemCount > previousItemCount) {
        attempt = 0; // Reset attempt counter
        delay = 500; // Reset delay to initial value
        previousItemCount = currentItemCount;
      } else {
        // Update previous count even if no new items (for next comparison)
        previousItemCount = currentItemCount;
      }
      
      // Call update callback if provided
      if (onUpdate) {
        onUpdate(response);
      }

      // Stop polling if response is in terminal state
      if (response.status === 'completed' || response.status === 'failed') {
        return result;
      }

      // Wait before next poll with exponential backoff (max 5 seconds)
      const currentDelay = delay;
      await new Promise(resolve => setTimeout(resolve, currentDelay));
      delay = Math.min(delay * 1.5, 5000);
    }

    // Max attempts reached, check if response is still in progress
		const lastResult = await this.getResponse(responseId);
    
    // If response is still in progress or queued, return an error to indicate polling stopped
    if (lastResult.success && lastResult.data) {
      if (lastResult.data.status === 'queued' || lastResult.data.status === 'in_progress') {
        return {
          success: false,
          error: 'Polling stopped: Response is still processing. The response may complete in the background. You can refresh the conversation to check for updates.',
          data: lastResult.data // Include the last known state for reference
        };
      }
    }
    
    return lastResult;
  }

  /**
   * Get a response by ID with all items.
   */
	async getResponse(responseId: string): Promise<ApiResponse<Response>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/responses/${encodeURIComponent(responseId)}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include'
			});

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: 'Response not found' };
        }
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get response' };
		}
	}

  /**
   * Helper function to create an EventSource-like interface for SSE streaming from container.
   * Since EventSource doesn't support POST, we use fetch and parse the stream manually.
   * @param containerAppUrl - The URL of the workspace-runtime container app
   */
  async createResponseStream(
    input: string,
    previousResponseId: string | null,
    containerAppUrl: string,
    onEvent: (event: ResponseStreamEvent) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): Promise<() => void> {
    const requestBody = {
      input,
      previous_response_id: previousResponseId,
      stream: true,
    };

    let abortController: AbortController | null = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    const cleanup = () => {
      if (reader) {
        reader.cancel().catch(() => {});
        reader = null;
      }
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    };

    try {
      // Get session token for cross-origin request
      const sessionToken = await this.getSessionToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${containerAppUrl}/responses`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody),
        signal: abortController.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processLines = (text: string) => {
        const lines = text.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6);
              const event: ResponseStreamEvent = JSON.parse(jsonStr);
              onEvent(event);
            } catch (error) {
              console.error('Failed to parse SSE event:', error, line);
            }
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();

        if (value && value.length > 0) {
          // Always process any bytes received, even when done is simultaneously true.
          // The ReadableStream spec allows a final chunk to be returned with done: true,
          // so processing before the done-check avoids silently dropping the last SSE frame
          // (e.g. conversation.topic.updated).
          buffer += decoder.decode(value, { stream: !done });
          processLines(buffer);
        }

        if (done) {
          // Flush any remaining buffer content that wasn't terminated by a newline
          if (buffer.trim()) {
            if (buffer.startsWith('data: ')) {
              try {
                const event: ResponseStreamEvent = JSON.parse(buffer.slice(6));
                onEvent(event);
              } catch (error) {
                console.error('Failed to parse final SSE event:', error, buffer);
              }
            }
            buffer = '';
          }
          if (onComplete) {
            onComplete();
          }
          break;
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Abort is expected when cleanup is called
        return cleanup;
      }
      if (onError) {
        onError(error instanceof Error ? error : new Error('Unknown error'));
      }
    }

    return cleanup;
  }

  /**
   * Process a single SSE event and extract item/status updates.
   * Returns the item to add (if any) and status update (if any).
   */
  processStreamEvent(
    event: ResponseStreamEvent
  ): { item?: ApiResponseItem; status?: 'in_progress' | 'completed' | 'failed'; error?: Response['error'] } {
    const now = Math.floor(Date.now() / 1000);
    const generateId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    switch (event.type) {
      case 'response.in_progress':
        return { status: 'in_progress' };

      case 'response.completed':
        return { status: 'completed' };

      case 'response.failed':
        const failedEvent = event as ResponseFailedEvent;
        return {
          status: 'failed',
          error: {
            code: failedEvent.error?.code || 'UNKNOWN_ERROR',
            message: failedEvent.error?.message || 'Response failed',
            details: failedEvent.error?.details || null,
          },
        };

      case 'response.message.done':
        const messageEvent = event as MessageDoneEvent;
        const messageItem: ApiMessageItem = {
          type: 'message',
          id: generateId('msg'),
          role: 'assistant',
          content: messageEvent.content || '',
          created_at: now,
        };
        return { item: messageItem };

      case 'response.tool_call.created':
        const toolCallEvent = event as ToolCallCreatedEvent;
        const toolCallItem: ApiToolCallItem = {
          type: 'tool_call',
          id: generateId('tcl'),
          agent_action_id: null,
          tool_name: toolCallEvent.tool_name || 'unknown',
          tool_type: toolCallEvent.tool_type || 'function',
          codex_tool_type: null, // Will be set from event data if available
          arguments: (event as any).arguments || {},
          status: 'in_progress',
          created_at: now,
        };
        // Try to extract codex_tool_type and arguments from event if available
        if ((event as any).codex_tool_type) {
          toolCallItem.codex_tool_type = (event as any).codex_tool_type;
        }
        if ((event as any).arguments) {
          toolCallItem.arguments = (event as any).arguments;
        }
        return { item: toolCallItem };

      case 'response.tool_output.done':
        const toolOutputEvent = event as ToolOutputDoneEvent;
        const toolOutputItem: ApiToolOutputItem = {
          type: 'tool_output',
          id: generateId('tou'),
          agent_action_id: null,
          tool_call_id: (event as any).tool_call_id || '', // May be in event data
          status: (event as any).status === 'failed' ? 'failed' : 'completed',
          output: toolOutputEvent.output,
          error: (event as any).error || null,
          created_at: now,
        };
        return { item: toolOutputItem };

      case 'response.reasoning.done':
        const reasoningEvent = event as ReasoningDoneEvent;
        const reasoningItem: ApiReasoningItem = {
          type: 'reasoning',
          id: generateId('rng'),
          agent_id: null,
          content: reasoningEvent.content || '',
          summary: (event as any).summary || null,
          created_at: now,
        };
        return { item: reasoningItem };

      case 'error':
        const errorEvent = event as ResponseErrorEvent;
        const errorItem: ApiErrorItem = {
          type: 'error',
          id: generateId('err'),
          error_code: errorEvent.error?.code || 'UNKNOWN_ERROR',
          message: errorEvent.error?.message || 'An error occurred',
          recoverable: true,
          details: errorEvent.error?.details || null,
          created_at: now,
        };
        return { item: errorItem };

      default:
        return {};
    }
  }

  /**
   * Get workspace files listing from workspace-runtime container
   * @param orgId - The Salesforce org ID
   * @param containerAppUrl - The URL of the workspace-runtime container app
   * @param path - Optional relative path from workspace root
   */
  async getWorkspaceFiles(
    containerAppUrl: string,
    path?: string
  ): Promise<ApiResponse<{ status: string; orgId: string; path: string; files: Array<{ name: string; type: 'file' | 'directory'; path: string }>; timestamp: string }>> {
    try {
      const params = new URLSearchParams();

      if (path) {
        params.append('path', path);
      }

      // Get session token for cross-origin request
      const sessionToken = await this.getSessionToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${containerAppUrl}/workspace/files?${params.toString()}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get workspace files' };
    }
  }

  /**
   * Get workspace file content from workspace-runtime container
   * @param orgId - The Salesforce org ID
   * @param containerAppUrl - The URL of the workspace-runtime container app
   * @param path - Relative path from workspace root to the file
   */
  async getWorkspaceFileContent(
    containerAppUrl: string,
    path: string
  ): Promise<ApiResponse<{ status: string; orgId: string; path: string; content: string; timestamp: string }>> {
    try {
      const params = new URLSearchParams({
        path: path,
      });

      // Get session token for cross-origin request
      const sessionToken = await this.getSessionToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${containerAppUrl}/workspace/files/content?${params.toString()}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get workspace file content' };
    }
  }

  /**
   * Get application configuration
   */
  async getAppConfig(): Promise<ApiResponse<{ agenticServicesPackage: { name: string; namespace: string; installUrl: string; password: string } }>> {
    try {
      const response = await fetch(`${await apiUrl()}/api/app/config`, {
        method: 'GET',
        headers: await getDefaultHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // Use errorText as-is if not JSON
        }
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get app configuration' };
    }
  }

	// ── Analysis Run Methods ──────────────────────────────────────────────

	/**
	 * List analysis runs for a snapshot
	 */
	async getAnalysisRuns(snapshotId: string): Promise<ApiResponse<AnalysisRunsResponse>> {
		try {
			const params = new URLSearchParams({ snapshotId });
			const response = await fetch(`${await apiUrl()}/api/analysis-runs?${params.toString()}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include',
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get analysis runs' };
		}
	}

	/**
	 * Get a single analysis run with step details
	 */
	async getAnalysisRun(runId: string): Promise<ApiResponse<AnalysisRunDetailResponse>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/analysis-runs/${encodeURIComponent(runId)}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include',
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get analysis run details' };
		}
	}

	/**
	 * Create a new analysis run
	 */
	async createAnalysisRun(snapshotId: string, workspaceId?: string): Promise<ApiResponse<CreateAnalysisRunResponse>> {
		try {
			const body: { snapshotId: string; workspaceId?: string } = { snapshotId };
			if (workspaceId) {
				body.workspaceId = workspaceId;
			}

			const response = await fetch(`${await apiUrl()}/api/analysis-runs`, {
				method: 'POST',
				headers: await getDefaultHeaders(),
				credentials: 'include',
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to create analysis run' };
		}
	}

	/**
	 * Get analysis results for a run by result type
	 */
	async getAnalysisResults(runId: string, resultType: string): Promise<ApiResponse<AnalysisResultsResponse>> {
		try {
			const response = await fetch(`${await apiUrl()}/api/analysis-runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(resultType)}`, {
				method: 'GET',
				headers: await getDefaultHeaders(),
				credentials: 'include',
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage = errorText;
				try {
					const errorJson = JSON.parse(errorText);
					errorMessage = errorJson.error || errorJson.message || errorText;
				} catch {
					// Use errorText as-is if not JSON
				}
				return { success: false, error: errorMessage };
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			return { success: false, error: error instanceof Error ? error.message : 'Failed to get analysis results' };
		}
	}

	/**
	 * Send an audio blob to the workspace-runtime transcription endpoint and return
	 * the transcribed text. Uses the same session-token auth as other container calls.
	 * @param audioBlob - Recorded audio data
	 * @param mimeType  - MIME type of the audio (e.g. audio/webm, audio/mp4)
	 * @param containerAppUrl - The workspace-runtime container URL
	 */
	async transcribeAudio(audioBlob: Blob, mimeType: string, containerAppUrl: string): Promise<string> {
		const sessionToken = await this.getSessionToken();
		const headers: HeadersInit = {};
		if (sessionToken) {
			headers['Authorization'] = `Bearer ${sessionToken}`;
		}

		// Derive a sensible filename extension from the MIME type
		const ext = mimeType.startsWith('audio/mp4') ? 'mp4'
			: mimeType.startsWith('audio/ogg') ? 'ogg'
			: 'webm';

		const formData = new FormData();
		formData.append('file', audioBlob, `recording.${ext}`);

		const response = await fetch(`${containerAppUrl}/transcribe`, {
			method: 'POST',
			headers,
			credentials: 'include',
			body: formData,
		});

		if (!response.ok) {
			const errorText = await response.text();
			let errorMessage = errorText;
			try {
				const errorJson = JSON.parse(errorText);
				errorMessage = errorJson.error || errorJson.message || errorText;
			} catch {
				// Use errorText as-is if not JSON
			}
			throw new Error(errorMessage || `Transcription request failed (${response.status})`);
		}

		const data = await response.json() as { text: string };
		return data.text;
	}

	/**
	 * List output documents for the current user from workspace-runtime container
	 * @param containerAppUrl - The URL of the workspace-runtime container app
	 * @param userId - The current user's ID (used to construct the output-docs path)
	 */
	async getOutputDocs(
		containerAppUrl: string,
		userId: string
	): Promise<ApiResponse<{ status: string; orgId: string; path: string; files: Array<{ name: string; type: 'file' | 'directory'; path: string }>; timestamp: string }>> {
		return this.getWorkspaceFiles(containerAppUrl, `output-docs/${userId}`);
	}

	/**
	 * Trigger a browser download of an output document
	 * @param containerAppUrl - The URL of the workspace-runtime container app
	 * @param filePath - Workspace-relative path to the file (e.g. output-docs/usr_123/summary.md)
	 */
	async downloadOutputDoc(containerAppUrl: string, filePath: string): Promise<void> {
		const sessionToken = await this.getSessionToken();
		const headers: HeadersInit = {};
		if (sessionToken) {
			headers['Authorization'] = `Bearer ${sessionToken}`;
		}

		const params = new URLSearchParams({ path: filePath });
		const response = await fetch(`${containerAppUrl}/workspace/files/download?${params.toString()}`, {
			method: 'GET',
			headers,
			credentials: 'include',
		});

		if (!response.ok) {
			const errorText = await response.text();
			let errorMessage = errorText;
			try {
				const errorJson = JSON.parse(errorText);
				errorMessage = errorJson.error || errorJson.message || errorText;
			} catch {
				// Use errorText as-is if not JSON
			}
			throw new Error(errorMessage || `Download failed (${response.status})`);
		}

		const blob = await response.blob();
		const filename = filePath.split('/').pop() || 'document.md';
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}

}

export const apiService = new ApiService();